/**
 * SELECT Query Configuration Component
 */

import * as React from 'react';
import { 
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { 
  SelectQueryOptions,
  OrderByClause,
} from '@/lib/engine';
import { 
  SelectQueryConfigProps, 
} from './types';
import { FilterBuilder } from './filter-builder';
import { ColumnSelector } from './select-query/column-selector';
import { QueryFilter } from './select-query/query-filter';

/**
 * SELECT Query Configuration Component
 */
export function SelectQueryConfig({ 
  query, 
  originalColumns, 
  updateQuery, 
  onSelectedItemClick 
}: SelectQueryConfigProps) {
  // Check if we should render the component
  const shouldRender = query.type === 'SELECT' && !!updateQuery;
  
  // If we shouldn't render, return null
  if (!shouldRender) return null;
  
  const options = query.options as SelectQueryOptions;
  
  // Get the selected columns
  const selectedColumns = options.columns || [];
  
  // Get the order by clauses
  const orderBy = options.orderBy || [];

  // Update query with new columns
  const handleColumnChange = (newColumns: any[]) => {
    if (!updateQuery) return;
    
    updateQuery({
      ...query,
      options: {
        ...options,
        columns: newColumns
      }
    });
  };
  
  // Update query with new where clause
  const handleWhereChange = (where: string) => {
    if (!updateQuery) return;
    
    updateQuery({
      ...query,
      options: {
        ...options,
        where
      }
    });
  };
  
  // Update query with new order by clauses
  const handleOrderByChange = (newOrderBy: OrderByClause[]) => {
    if (!updateQuery) return;
    
    updateQuery({
      ...query,
      options: {
        ...options,
        orderBy: newOrderBy
      }
    });
  };

  return (
    <div className="h-full flex flex-col">
      <Tabs defaultValue="columns" className="w-full h-full flex flex-col">
        <TabsList className="flex w-full h-8 mb-2">
          <TabsTrigger className="flex-1 h-full text-xs" value="columns">
            Columns & Sort
            <div className="flex items-center gap-1 ml-1.5">
              {selectedColumns.length > 0 && (
                <Badge variant="secondary" className="text-xs px-1 py-0">{selectedColumns.length}</Badge>
              )}
              {orderBy.length > 0 && (
                <Badge variant="secondary" className="text-xs px-1 py-0">↕ {orderBy.length}</Badge>
              )}
            </div>
          </TabsTrigger>
          <TabsTrigger className="flex-1 h-full text-xs" value="filters">Filters</TabsTrigger>
        </TabsList>
        
        <TabsContent value="columns" className="flex-1 overflow-hidden">
          <ColumnSelector
            originalColumns={originalColumns}
            selectedColumns={selectedColumns}
            orderBy={orderBy}
            onColumnChange={handleColumnChange}
            onOrderByChange={handleOrderByChange}
            onSelectedItemClick={onSelectedItemClick}
          />
        </TabsContent>
        
        <TabsContent value="filters" className="flex-1 overflow-hidden">
          <QueryFilter
            originalColumns={originalColumns}
            whereClause={options.where || ''}
            onWhereChange={handleWhereChange}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
} 